import { useState } from "react";
import { login, login2fa, init2fa } from "../api/auth";
import Setup2FAPage from "./Setup2FAPage";

type Mode = "login" | "2fa" | "setup2fa";

export default function LoginPage() {
  const [mode, setMode] = useState<Mode>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [userId, setUserId] = useState<number | null>(null);
  const [setupUri, setSetupUri] = useState<string | null>(null);
  const [setupSecret, setSetupSecret] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (mode === "setup2fa" && userId !== null) {
    return (
      <Setup2FAPage
        userId={userId}
        provisioningUri={setupUri}
        secret={setupSecret}
        onDone={() => window.location.reload()}
      />
    );
  }

  const submitLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await login(email, password);
      if (r.kind === "needs_2fa") {
        setUserId(r.userId);
        setMode("2fa");
      } else if (r.kind === "needs_setup") {
        // Admin-invited user logging in for the first time — fetch their
        // provisioning URI + secret using the same password they just entered.
        const init = await init2fa(email, password);
        setUserId(init.userId);
        setSetupUri(init.totpProvisioningUri);
        setSetupSecret(init.totpSecret);
        setMode("setup2fa");
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const submit2fa = async (e: React.FormEvent) => {
    e.preventDefault();
    if (userId === null) return;
    setBusy(true);
    setError(null);
    try {
      await login2fa(userId, code.trim());
      window.location.reload();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-screen">
      <div className="auth-card">
        <h1>Ptero-Cloud AI</h1>
        {mode === "login" && (
          <form onSubmit={submitLogin}>
            <label>
              Email
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
                autoComplete="email"
              />
            </label>
            <label>
              Password
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
              />
            </label>
            {error && <p className="auth-error">{error}</p>}
            <button type="submit" disabled={busy || !email || !password}>
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>
        )}
        {mode === "2fa" && (
          <form onSubmit={submit2fa}>
            <p>Enter the 6-digit code from your authenticator app.</p>
            <label>
              Code
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                required
                autoFocus
                autoComplete="one-time-code"
              />
            </label>
            {error && <p className="auth-error">{error}</p>}
            <button type="submit" disabled={busy || code.length !== 6}>
              {busy ? "Verifying…" : "Verify"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
